import React, { useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { animateTitle, animateContent, cleanupScrollTriggers } from "./gsap";
import { BUSINESS_NAME, BASE_URL } from "@/core/constants/business";

const PrivacyPolicy: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Meta data for SEO
  const META = {
    title: `Privacy Policy | ${BUSINESS_NAME}`,
    meta_description: `Learn about ${BUSINESS_NAME}'s privacy policy, including how we collect, use, and protect your personal information when you visit our Washington DC cannabis dispensary.`,
    canonicalUrl: "/privacy-policy",
    keywords: `${BUSINESS_NAME} privacy policy, cannabis dispensary privacy, data protection marijuana dispensary, customer information protection, DC dispensary privacy policy, cannabis customer privacy`,
  };

  // Rich snippet data for SEO
  const generatePrivacyPolicyRichSnippet = () => {
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE_URL}/privacy-policy/#webpage`,
      url: `${BASE_URL}/privacy-policy/`,
      name: META.title,
      description: META.meta_description,
      isPartOf: {
        "@id": `${BASE_URL}/#website`,
      },
      about: {
        "@type": "Organization",
        name: BUSINESS_NAME,
        url: BASE_URL,
      },
      mainContentOfPage: {
        "@type": "WebPageElement",
        cssSelector: ".privacy-policy-content",
      },
    };
  };

  const richSnippet = generatePrivacyPolicyRichSnippet();

  useEffect(() => {
    // Apply animations after a small delay to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && titleRef.current && contentRef.current) {
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );
        animateContent(
          { current: contentRef.current },
          { current: sectionRef.current }
        );
      }
    }, 100);

    // Clean up animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{META.title}</title>
        <meta name="description" content={META.meta_description} />
        <meta name="keywords" content={META.keywords} />
        <link
          rel="canonical"
          href={`https://slsdispensary.com${META.canonicalUrl}`}
        />

        {/* OpenGraph Meta Tags */}
        <meta property="og:title" content={META.title} />
        <meta property="og:description" content={META.meta_description} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://slsdispensary.com${META.canonicalUrl}`}
        />
        <meta
          property="og:image"
          content="https://slsdispensary.com/images/street-lawyer-services-dc-dispensary-entrance.jpg"
        />
        <meta property="og:site_name" content={BUSINESS_NAME} />

        {/* JSON-LD for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify(richSnippet)}
        </script>
      </Helmet>

      {/* Main Content */}
      <section ref={sectionRef} className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h1
            ref={titleRef}
            className="text-4xl md:text-5xl font-bold mb-8 text-center"
          >
            Privacy Policy
          </h1>

          <div
            ref={contentRef}
            className="max-w-3xl mx-auto prose prose-lg privacy-policy-content"
          >
            <p className="text-xl text-gray-700 mb-8">
              At {BUSINESS_NAME}, we value your privacy and are committed to
              protecting your personal information. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when
              you visit our dispensary, use our website, or engage with us in
              any way.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Information We Collect
            </h2>

            <h3 className="text-2xl font-bold mt-8 mb-4">
              Personal Information
            </h3>
            <p>
              We may collect personal information that you voluntarily provide
              to us when you:
            </p>
            <ul>
              <li>Visit our dispensary and make a purchase</li>
              <li>Sign up for our newsletter</li>
              <li>Create an account on our website</li>
              <li>Contact us with inquiries or feedback</li>
              <li>Participate in promotions or surveys</li>
            </ul>
            <p>
              This information may include your name, email address, phone
              number, date of birth, and delivery address (if applicable).
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">
              Age Verification Information
            </h3>
            <p>
              As a cannabis dispensary, we are legally required to verify that
              all customers are 21 years of age or older. We may collect and
              verify your age through government-issued identification.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">
              Automatically Collected Information
            </h3>
            <p>
              When you visit our website, our servers may automatically log
              standard data provided by your web browser. This may include your
              device's IP address, browser type and version, pages you visit,
              time and date of your visit, time spent on each page, and other
              details about your visit.
            </p>
            <p>
              We also collect information through cookies and similar
              technologies. You can manage cookie preferences through your
              browser settings.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process transactions and fulfill orders</li>
              <li>Verify your age and identity as required by law</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send transactional communications and order updates</li>
              <li>
                Share promotional offers, newsletters, and updates (if you've
                opted in)
              </li>
              <li>Improve our products, services, and website</li>
              <li>Analyze customer preferences and trends</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Information Sharing and Disclosure
            </h2>
            <p>
              We respect your privacy and will not sell your personal
              information. We may share your information in the following
              circumstances:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> We may share information
                with third-party vendors who provide services on our behalf,
                such as payment processing, website hosting, and marketing
                assistance.
              </li>
              <li>
                <strong>Business Transfers:</strong> If we undergo a merger,
                acquisition, or sale of all or a portion of our assets, your
                information may be transferred as part of that transaction.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose your
                information if required to do so by law or in response to valid
                requests by public authorities.
              </li>
              <li>
                <strong>Protection of Rights:</strong> We may disclose
                information to protect our rights, privacy, safety, or property,
                or that of our customers or others.
              </li>
            </ul>

            <h2 className="text-3xl font-bold mt-12 mb-6">Data Security</h2>
            <p>
              We implement appropriate security measures to protect against
              unauthorized access, alteration, disclosure, or destruction of
              your personal information. However, no method of transmission over
              the Internet or electronic storage is 100% secure, and we cannot
              guarantee absolute security.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Your Rights and Choices
            </h2>
            <p>
              Depending on your location, you may have certain rights regarding
              your personal information, which may include:
            </p>
            <ul>
              <li>Accessing your personal information</li>
              <li>Correcting inaccurate information</li>
              <li>Requesting deletion of your information</li>
              <li>Opting out of marketing communications</li>
              <li>Restricting certain data processing</li>
            </ul>
            <p>
              To exercise these rights or ask questions about our privacy
              practices, please contact us using the information provided below.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Children's Privacy
            </h2>
            <p>
              Our services are not directed to individuals under the age of 21.
              We do not knowingly collect personal information from individuals
              under 21 years of age. If we become aware that a person under 21
              has provided us with personal information, we will take steps to
              delete such information.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Changes to This Privacy Policy
            </h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify
              you of any changes by posting the new Privacy Policy on this page
              and updating the effective date. You are advised to review this
              Privacy Policy periodically for any changes.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our
              privacy practices, please contact us at:
            </p>
            <p>
              {BUSINESS_NAME}
              <br />
              3400 Connecticut Avenue NW
              <br />
              Washington, DC 20008
              <br />
              Phone: (202) 340-8277
              <br />
              Email: privacy@streetlawyerservices.com
            </p>

            <p className="text-sm text-gray-500 mt-8 italic">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
